import { doc, getDoc, setDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { firestore } from "../firebase";

function toInt(value, fallback = 0) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function getMaterialIdFromOtMaterial(material) {
    return toInt(material?.materialId ?? material?.id, NaN);
}

function getQuantityFromOtMaterial(material) {
    return toInt(material?.cantidad ?? material?.value, 0);
}

function buildMaterialQuantityMap(materiales = []) {
    const map = new Map();
    (materiales || []).forEach((material) => {
        const materialId = getMaterialIdFromOtMaterial(material);
        if (!Number.isFinite(materialId)) return;
        map.set(materialId, (map.get(materialId) || 0) + getQuantityFromOtMaterial(material));
    });
    return map;
}

function computeDelta(oldMateriales = [], newMateriales = []) {
    const oldMap = buildMaterialQuantityMap(oldMateriales);
    const newMap = buildMaterialQuantityMap(newMateriales);
    const allIds = new Set([...oldMap.keys(), ...newMap.keys()]);
    const deltas = [];

    allIds.forEach((materialId) => {
        const delta = (newMap.get(materialId) || 0) - (oldMap.get(materialId) || 0);
        if (delta !== 0) {
            deltas.push({ materialId, delta });
        }
    });

    return deltas;
}

async function applyPreQuantityDelta(cuadrillaId, deltas) {
    const idCuadrilla = toInt(cuadrillaId, NaN);
    if (!Number.isFinite(idCuadrilla) || !deltas.length) return;

    await Promise.all(deltas.map(async ({ materialId, delta }) => {
        const idMaterial = toInt(materialId, NaN);
        if (!Number.isFinite(idMaterial) || delta === 0) return;

        const docId = `${idCuadrilla}c${idMaterial}`;
        const docRef = doc(firestore, 'materialsxcuadrilla', docId);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
            const currentPre = toInt(snapshot.data().preQuantity, 0);
            const nextPre = Math.max(0, currentPre + delta);
            await updateDoc(docRef, { preQuantity: nextPre });
            return;
        }

        if (delta > 0) {
            await setDoc(docRef, {
                cantidad: 0,
                preQuantity: delta,
                idCuadrilla,
                idMaterial,
            });
        }
    }));
}

export async function syncPreQuantityForOrdenUpdate(cuadrillaId, oldMateriales, newMateriales) {
    const deltas = computeDelta(oldMateriales, newMateriales);
    await applyPreQuantityDelta(cuadrillaId, deltas);
}

export async function releasePreQuantityForOrden(cuadrillaId, materiales = []) {
    const map = buildMaterialQuantityMap(materiales);
    const deltas = [...map.entries()].map(([materialId, qty]) => ({
        materialId,
        delta: -qty,
    }));
    await applyPreQuantityDelta(cuadrillaId, deltas);
}

export async function recalculatePreQuantityFromCuadrillas(cuadrillas = []) {
    const totals = new Map();

    (cuadrillas || []).forEach((cuadrilla) => {
        const idCuadrilla = toInt(cuadrilla?.id, NaN);
        if (!Number.isFinite(idCuadrilla)) return;

        (cuadrilla?.ordenes || []).forEach((orden) => {
            const map = buildMaterialQuantityMap(orden?.materiales);
            map.forEach((qty, materialId) => {
                const key = `${idCuadrilla}c${materialId}`;
                totals.set(key, {
                    idCuadrilla,
                    idMaterial: materialId,
                    preQuantity: (totals.get(key)?.preQuantity || 0) + qty,
                });
            });
        });
    });

    const snapshot = await getDocs(collection(firestore, 'materialsxcuadrilla'));
    const updates = snapshot.docs.map(async (materialDoc) => {
        const data = materialDoc.data();
        const idCuadrilla = toInt(data.idCuadrilla, NaN);
        const idMaterial = toInt(data.idMaterial, NaN);
        if (!Number.isFinite(idCuadrilla) || !Number.isFinite(idMaterial)) return;

        const key = `${idCuadrilla}c${idMaterial}`;
        const preQuantity = totals.get(key)?.preQuantity || 0;
        await updateDoc(materialDoc.ref, { preQuantity });
        totals.delete(key);
    });

    await Promise.all(updates);

    const remaining = [...totals.values()].map(async ({ idCuadrilla, idMaterial, preQuantity }) => {
        const docId = `${idCuadrilla}c${idMaterial}`;
        const docRef = doc(firestore, 'materialsxcuadrilla', docId);
        const materialSnapshot = await getDoc(docRef);

        if (materialSnapshot.exists()) {
            await updateDoc(docRef, { preQuantity });
            return;
        }

        await setDoc(docRef, {
            cantidad: 0,
            preQuantity,
            idCuadrilla,
            idMaterial,
        });
    });

    await Promise.all(remaining);
}
